// What the editor does: open a mark, save it, delete it, move it. Every save is one undo step.

import { effect } from "@preact/signals";
import { cuesAfter, cuesUntilNextStandby, type DocIndex, sortDepartments, standbyCoverage, standbyText } from "../format/derive.ts";
import { newId } from "../format/ids.ts";
import { compareCueNumbers, isValidCueNumber, normalizeCueNumber, suggestCueNumber } from "../format/numbers.ts";
import type { Anchor, Cue, Moment, Note, NoteLayer, RegionAnchor, Standby } from "../format/types.ts";
import { insideNoteLayout } from "../layout/layout.ts";
import { type Change, commit, del, put } from "./edits.ts";
import { allDrafts, deleteDraft, putDraft } from "./idb.ts";
import { canEdit, closingPanel, type CueRowForm, ix, lastDepartments, type MarkRef, notify, panel, type PanelForm, selected, tabId } from "./state.ts";
import { shifted } from "./viewer.ts";

export const LAYER_SUGGESTIONS: Omit<NoteLayer, "id" | "order">[] = [
  { name: "Director", color: "#9333ea" },
  { name: "Blocking", color: "#475569" },
  { name: "Rewrites", color: "#dc2626" },
];
// Catppuccin Latte accents, using basic names in the department picker.
export const COLOR_SUGGESTIONS = [
  { name: "Red", color: "#d20f39" },
  { name: "Orange", color: "#fe640b" },
  { name: "Yellow", color: "#df8e1d" },
  { name: "Green", color: "#40a02b" },
  { name: "Teal", color: "#179299" },
  { name: "Blue", color: "#1e66f5" },
  { name: "Purple", color: "#8839ef" },
  { name: "Pink", color: "#ea76cb" },
] as const;

/** A color not yet used by any department or layer, for something new. */
export function freshColor(index: DocIndex): string {
  const used = new Set([...index.doc.departments.map((d) => d.color), ...index.doc.noteLayers.map((l) => l.color)]);
  return (COLOR_SUGGESTIONS.find((c) => !used.has(c.color)) ?? COLOR_SUGGESTIONS[used.size % COLOR_SUGGESTIONS.length] ?? COLOR_SUGGESTIONS[0]).color;
}

/**
 * The suggested last cue for a standby at `anchor`: the last cue before the next standby, looking no
 * further than the next page; else the first cue after it; else "" (nothing after it yet).
 */
export function suggestThrough(index: DocIndex, anchor: Anchor, id: string): string {
  const at = { id: id || "s-~", anchor };
  return (cuesUntilNextStandby(index, at, 1).at(-1) ?? cuesAfter(index, at)[0])?.id ?? "";
}

/**
 * When cues are deleted, a standby whose last cue is among them moves its end back to the last
 * cue it still covers (or to none), so it keeps warning what's left of its sequence.
 */
export function retargetStandbys(index: DocIndex, deleted: ReadonlySet<string>): Change[] {
  const changes: Change[] = [];
  for (const info of standbyCoverage(index)) {
    if (!deleted.has(info.standby.through)) continue;
    const keep = info.covered.filter((c) => !deleted.has(c.id)).at(-1)?.id ?? "";
    changes.push(put("standbys", { ...info.standby, through: keep }, info.standby));
  }
  return changes;
}

/** The prefilled number for a new cue of `department` at `anchor`, from its neighbours in show order. */
export function suggestNumber(index: DocIndex, department: string, anchor: Anchor, momentId: string): string {
  const here = { id: momentId || "m-~", anchor };
  let prev: Cue | null = null;
  let next: Cue | null = null;
  for (const c of index.showCues()) {
    if (c.department !== department) continue;
    const m = index.moments.get(c.moment);
    if (!m || m.id === momentId) continue;
    if (index.compareAnchored(m, here) < 0) prev = c;
    else if (!next) next = c;
  }
  return suggestCueNumber(prev?.number ?? null, next?.number ?? null);
}

function emptyForm(anchor: Anchor): PanelForm {
  // Always a cue: a standby or note is a deliberate second tap, so a stray mark never becomes one.
  return { mode: "new", kind: "cue", anchor, id: "", rows: [], scriptText: "", layer: null, newLayerName: "", style: "box", placement: "margin", text: "", error: null, restored: false, through: "", originals: { cues: [] } };
}

export function openNew(anchor: Anchor): void {
  const index = ix.value;
  if (!index || !canEdit.value) return;
  const known = lastDepartments.value.filter((d) => index.departments.has(d));
  const depts = known.length ? known : index.sortedDepartments.slice(0, 1).map((d) => d.id);
  const form = emptyForm(anchor);
  form.rows = depts.map((department) => ({ department, cueId: null, number: suggestNumber(index, department, anchor, ""), label: "", notes: "" }));
  form.layer = index.sortedLayers[0]?.id ?? null;
  selected.value = null;
  panel.value = form;
}

export function openEdit(ref: MarkRef): void {
  const index = ix.value;
  if (!index) return;
  selected.value = ref;
  if (ref.kind === "moment") {
    const m = index.moments.get(ref.id);
    if (!m) return;
    const cues = index.cuesOf(m.id);
    const rows: CueRowForm[] = cues.map((c) => ({ department: c.department, cueId: c.id, number: c.number, label: c.label, notes: c.notes }));
    panel.value = { ...emptyForm(m.anchor), mode: "edit", kind: "cue", id: m.id, rows, scriptText: m.scriptText, originals: { moment: m, cues } };
  } else if (ref.kind === "standby") {
    const s = index.doc.standbys.find((x) => x.id === ref.id);
    if (s) panel.value = { ...emptyForm(s.anchor), mode: "edit", kind: "standby", id: s.id, through: s.through, originals: { standby: s, cues: [] } };
  } else {
    const n = index.doc.notes.find((x) => x.id === ref.id);
    if (n) panel.value = { ...emptyForm(n.anchor), mode: "edit", kind: "note", id: n.id, layer: n.layer, style: n.style, placement: n.placement ?? "margin", text: n.text, originals: { note: n, cues: [] } };
  }
}

export async function closePanel(): Promise<void> {
  if (closingPanel.value) return;
  closingPanel.value = true;
  clearTimeout(draftTimer);
  try {
    await deleteDraft(tabId);
  } catch (error) {
    notify(`This device couldn't clear the closed draft (${(error as Error).message}). Reloading may restore it.`, "warn");
  } finally {
    panel.value = null;
    selected.value = null;
    closingPanel.value = false;
  }
}

export function toggleDepartment(department: string): void {
  const form = panel.value;
  const index = ix.value;
  if (!form || !index) return;
  if (form.rows.some((r) => r.department === department)) {
    const rows = form.rows.filter((r) => r.department !== department);
    if (!rows.length) {
      panel.value = { ...form, error: form.mode === "edit" ? "A cue needs at least one department. To remove the whole mark, use Delete." : "Pick at least one department." };
      return;
    }
    panel.value = { ...form, rows, error: null };
  } else {
    const row = { department, cueId: null, number: suggestNumber(index, department, form.anchor, form.id), label: "", notes: "" };
    const order = new Map(sortDepartments(index.doc.departments).map((d, i) => [d.id, i]));
    const rows = [...form.rows, row].sort((a, b) => (order.get(a.department) ?? 0) - (order.get(b.department) ?? 0));
    panel.value = { ...form, rows, error: null };
  }
}

const fail = (form: PanelForm, error: string): void => {
  panel.value = { ...form, error };
};

/** Close the editor, say what was saved, and outline the mark for a moment so it can be found. */
function saved(ref: MarkRef, message: string): void {
  void closePanel().then(() => {
    selected.value = ref;
    setTimeout(() => {
      // Only expire this save's feedback, not a later explicit selection or another save.
      if (selected.value === ref && !panel.value) selected.value = null;
    }, 2500);
    notify(message);
  });
}

export function savePanel(): void {
  const form = panel.value;
  const index = ix.value;
  if (!form || !index || closingPanel.value) return;
  if (!canEdit.value) return fail(form, "The project is read-only.");
  if (form.kind === "cue") return saveCue(form, index);
  if (form.kind === "standby") {
    const before = form.originals.standby;
    const id = form.mode === "edit" ? form.id : newId("standbys");
    const record: Standby = { id, anchor: form.anchor, through: form.through || suggestThrough(index, form.anchor, id) };
    commit(form.mode === "edit" ? "Edit standby" : "New standby", [put("standbys", record, before)]);
    const after = ix.value;
    const info = after ? standbyCoverage(after).find((s) => s.standby.id === record.id) : undefined;
    const text = after && info ? standbyText(after, info.covered) : null;
    return saved({ kind: "standby", id: record.id }, text && text !== "SB —" ? `Standby saved: ${text}` : "Standby saved. It covers no cues yet: move it above the cues it should call.");
  }
  if (form.placement === "inside") {
    if (form.style !== "box") return fail(form, "Inside-box text needs the Box style.");
    const { error } = insideNoteLayout(index.page(form.anchor.page), form.anchor, form.text.trim(), "#212121");
    if (error) return fail(form, error);
  }
  const changes: Change[] = [];
  let layer = form.layer;
  if (!layer) {
    const name = form.newLayerName.trim();
    if (!name) return fail(form, "Pick a note layer, or name a new one.");
    const existing = index.doc.noteLayers.find((l) => l.name.toLowerCase() === name.toLowerCase());
    if (existing) layer = existing.id;
    else {
      const suggestion = LAYER_SUGGESTIONS.find((s) => s.name.toLowerCase() === name.toLowerCase());
      const created: NoteLayer = {
        id: newId("noteLayers"),
        name,
        color: suggestion?.color ?? freshColor(index),
        order: Math.max(0, ...index.doc.noteLayers.map((l) => l.order)) + 1,
      };
      changes.push(put("noteLayers", created));
      layer = created.id;
    }
  }
  const before = form.originals.note;
  const note: Note = { id: form.mode === "edit" ? form.id : newId("notes"), layer, anchor: form.anchor, style: form.style, text: form.text.trim(), ...(form.placement === "inside" ? { placement: "inside" as const } : {}) };
  changes.push(put("notes", note, before));
  commit(form.mode === "edit" ? "Edit note" : "New note", changes);
  saved({ kind: "note", id: note.id }, `Note saved in ${index.layers.get(layer)?.name ?? form.newLayerName.trim()}`);
}

function saveCue(form: PanelForm, index: DocIndex): void {
  if (!form.rows.length) return fail(form, "Pick at least one department.");
  const rows = form.rows.map((r) => ({ ...r, number: normalizeCueNumber(r.number) }));
  const mine = new Set(rows.map((r) => r.cueId).filter(Boolean));
  for (const [i, r] of rows.entries()) {
    const code = index.code(r.department);
    if (!r.number) return fail(form, `${code} needs a cue number.`);
    if (!isValidCueNumber(r.number)) return fail(form, `"${r.number}" isn't a cue number. Use digits with an optional decimal (14, 14.5) or letter (5A), or letters only (A).`);
    const clash = index.doc.cues.find((c) => c.department === r.department && !mine.has(c.id) && compareCueNumbers(c.number, r.number) === 0);
    if (clash) return fail(form, `${code} ${clash.number} already exists.`);
    if (rows.some((o, j) => j < i && o.department === r.department && compareCueNumbers(o.number, r.number) === 0)) return fail(form, `${code} ${r.number} is entered twice.`);
  }
  const momentId = form.mode === "edit" ? form.id : newId("moments");
  const beforeMoment = form.originals.moment;
  const moment: Moment = { id: momentId, anchor: form.anchor, scriptText: form.scriptText.trim() };
  const changes: Change[] = [put("moments", moment, beforeMoment)];
  // Only what this editor opened with: cues another device added meanwhile are left alone.
  const existing = form.originals.cues;
  for (const r of rows) {
    const before = existing.find((c) => c.id === r.cueId);
    const cue: Cue = { id: r.cueId ?? newId("cues"), moment: momentId, department: r.department, number: r.number, label: r.label.trim(), notes: r.notes.trim() };
    changes.push(put("cues", cue, before));
  }
  const removed = existing.filter((c) => !rows.some((r) => r.cueId === c.id));
  for (const c of removed) changes.push(del("cues", c));
  changes.push(...retargetStandbys(index, new Set(removed.map((c) => c.id))));
  const refs = rows.map((r) => `${index.code(r.department)} ${r.number}`).join(" · ");
  commit(form.mode === "edit" ? `Edit ${refs}` : `New ${refs}`, changes);
  if (form.mode === "new") lastDepartments.value = rows.map((r) => r.department);
  saved({ kind: "moment", id: momentId }, "Saved");
}

/** Delete the open mark (a moment with all its cues, a standby or a note): one undoable edit. */
export function deleteOpen(): void {
  const form = panel.value;
  const index = ix.value;
  if (!form || !index || form.mode !== "edit" || closingPanel.value) return;
  if (form.kind === "cue") {
    const m = index.moments.get(form.id);
    if (!m) {
      void closePanel();
      return;
    }
    const cues = index.cuesOf(m.id);
    commit(`Delete ${cues.map((c) => index.cueRef(c)).join(" · ") || "mark"}`, [
      ...cues.map((c) => del("cues", c)),
      del("moments", m),
      ...retargetStandbys(index, new Set(cues.map((c) => c.id))),
    ]);
  } else if (form.kind === "standby") {
    const s = index.doc.standbys.find((x) => x.id === form.id);
    if (s) commit("Delete standby", [del("standbys", s)]);
  } else {
    const n = index.doc.notes.find((x) => x.id === form.id);
    if (n) commit("Delete note", [del("notes", n)]);
  }
  closePanel();
}

/** Drag-to-move: shift the whole anchor, within its page. */
export function moveMark(ref: MarkRef, dx: number, dy: number): void {
  const index = ix.value;
  if (!index || (!dx && !dy)) return;
  const form = panel.value;
  if (ref.kind === "moment") {
    const m = index.moments.get(ref.id);
    if (!m) return;
    const after = { ...m, anchor: shifted(m.anchor, dx, dy) };
    commit(`Move ${index.cuesOf(m.id).map((c) => index.cueRef(c)).join(" · ") || "mark"}`, [put("moments", after, m)]);
    if (form?.mode === "edit" && form.id === m.id) panel.value = { ...form, anchor: after.anchor, originals: { ...form.originals, moment: after } };
  } else if (ref.kind === "standby") {
    const s = index.doc.standbys.find((x) => x.id === ref.id);
    if (!s) return;
    const after = { ...s, anchor: shifted(s.anchor, dx, dy) };
    commit("Move standby", [put("standbys", after, s)]);
    if (form?.mode === "edit" && form.id === s.id) panel.value = { ...form, anchor: after.anchor, originals: { ...form.originals, standby: after } };
  } else {
    const n = index.doc.notes.find((x) => x.id === ref.id);
    if (!n) return;
    const after = { ...n, anchor: shifted(n.anchor, dx, dy) };
    commit("Move note", [put("notes", after, n)]);
    if (form?.mode === "edit" && form.id === n.id) panel.value = { ...form, anchor: after.anchor, originals: { ...form.originals, note: after } };
  }
  selected.value = ref;
}

function sameRegion(anchor: Anchor, other: RegionAnchor): boolean {
  return anchor === other || (anchor.shape === "region" && anchor.page === other.page && anchor.rects.length === other.rects.length
    && anchor.rects.every((rect, i) => rect.every((coordinate, j) => coordinate === other.rects[i]![j])));
}

/** A finished resize is one undoable edit; abandon stale drags rather than replacing newer geometry. */
export function resizeMark(ref: MarkRef, before: RegionAnchor, after: RegionAnchor): void {
  const index = ix.value;
  if (!index || !canEdit.value || panel.value || selected.value?.kind !== ref.kind || selected.value.id !== ref.id || sameRegion(before, after)) return;
  if (ref.kind === "moment") {
    const m = index.moments.get(ref.id);
    if (!m || !sameRegion(m.anchor, before)) return;
    commit(`Resize ${index.cuesOf(m.id).map((c) => index.cueRef(c)).join(" · ") || "mark"}`, [put("moments", { ...m, anchor: after }, m)]);
  } else if (ref.kind === "standby") {
    const s = index.doc.standbys.find((x) => x.id === ref.id);
    if (!s || !sameRegion(s.anchor, before)) return;
    commit("Resize standby", [put("standbys", { ...s, anchor: after }, s)]);
  } else {
    const n = index.doc.notes.find((x) => x.id === ref.id);
    if (!n || !sameRegion(n.anchor, before)) return;
    if (n.placement === "inside") {
      const { error, warning } = insideNoteLayout(index.page(after.page), after, n.text, index.layers.get(n.layer)?.color ?? "#212121");
      if (error) {
        notify(error, "warn");
        return;
      }
      if (warning) notify(warning, "warn");
    }
    commit("Resize note", [put("notes", { ...n, anchor: after }, n)]);
  }
}

// ---------------------------------------------------------------- the open editor survives a reload

let draftTimer: ReturnType<typeof setTimeout> | undefined;

/** Finish the current draft write before leaving the editor for sign-out. */
export async function flushDraft(): Promise<void> {
  clearTimeout(draftTimer);
  const form = panel.value;
  if (form) await putDraft({ tab: tabId, at: Date.now(), state: form });
}

export function mirrorDrafts(): void {
  effect(() => {
    clearTimeout(draftTimer);
    if (!panel.value) return;
    draftTimer = setTimeout(() => {
      void flushDraft().catch(() => {});
    }, 300);
  });
}

/** Reopen an editor that was open when this tab (or a closed tab on this device) went away. */
export async function restoreDraft(): Promise<void> {
  const drafts = (await allDrafts().catch(() => [])).sort((a, b) => b.at - a.at);
  const draft = drafts.find((d) => d.tab === tabId) ?? drafts.find((d) => Date.now() - Number(localStorage.getItem(`q2q-hb-${d.tab}`) ?? 0) > 15000);
  if (!draft) return;
  if (draft.tab !== tabId) await deleteDraft(draft.tab).catch(() => {});
  const form = draft.state as PanelForm;
  if (!form?.anchor) return;
  panel.value = { ...form, placement: form.placement ?? "margin", restored: true, error: null };
  notify("Restored an unsaved edit.", "info");
}
